import { ScrollPositionDirective } from '@shared/directives/scroll-position';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ButtonSize,
  MarkdownPipe,
  MibBreakpointService,
  MibButtonDirective,
  MibIconComponent,
  MibTooltipDirective,
  MibDotsLoaderComponent,
} from '@medinbox/angular';
import { ChatEmptyScreen } from './chat-empty-screen';
import { PromptBox } from './prompt-box';
import { ChatAIWaitingComponent } from './chat-waiting';
import { ChatService } from './chat.service';
import { AnalyticsEvents, CASE_LIBRARY_SEARCH_CHAT_ID, Message, MessageRole } from '@shared/models';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { filter, map, pairwise } from 'rxjs';
import { AnalyticsService } from '@core/services';
import { marked } from 'marked';

export interface ChatInfo {
  chatId: string;
  assistantIntro?: string;
  assistantSubtext?: string;
  hideVideoCutPrompt?: boolean;
}

@Component({
  selector: 'mai-chat',
  imports: [
    ScrollPositionDirective,
    ChatEmptyScreen,
    PromptBox,
    MarkdownPipe,
    ChatAIWaitingComponent,
    MibDotsLoaderComponent,
    MibButtonDirective,
    MibIconComponent,
    MibTooltipDirective,
  ],
  templateUrl: './chat.html',
  styleUrl: './chat.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class Chat {
  protected readonly service = inject(ChatService);
  private readonly analytics = inject(AnalyticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpointService = inject(MibBreakpointService);

  readonly chatInfo = input.required<ChatInfo | null>();
  readonly chatReady = input(false, { transform: booleanAttribute });
  readonly active = input(true, { transform: booleanAttribute });

  readonly timeLinkClick = output<string>();

  readonly messageContainer = viewChild<ElementRef<HTMLDivElement>>('messageContainer');

  readonly copyTooltip = signal('Copy');
  protected readonly copyButtonSize = computed<ButtonSize>(() =>
    this.breakpointService.sizeClass() !== 'expanded' ? 'sm' : 'md',
  );
  readonly isOverflowing = signal(false);

  readonly MessageRole = MessageRole;

  readonly promptExamples = this.service.promptExamples;
  readonly hitlMessage = this.service.hitlMessage;
  readonly chatLocked = this.service.chatLocked;
  readonly messages = this.service.filteredMessages;
  readonly isThinking = this.service.isThinking;

  private firstLoadDone = false;
  private pendingScroll: 'bottom' | { displayLast: boolean } | null = null;

  constructor() {
    effect(() => {
      if (this.hitlMessage()) {
        this.requestScroll({ displayLast: true });
      }
    });

    // Scroll requests that happen while the chat is hidden (e.g. behind another
    // mobile tab) can't be applied because a display:none container has no layout.
    // Remember the latest intent and flush it as soon as the chat becomes visible.
    effect(() => {
      if (this.active() && this.pendingScroll) {
        const intent = this.pendingScroll;
        this.pendingScroll = null;
        this.performScroll(intent);
      }
    });

    toObservable(this.chatInfo)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((chatInfo) => !!chatInfo),
      )
      .subscribe((chatInfo) => this.service.initChat(chatInfo, this.destroyRef));

    toObservable(this.messages)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((messages) => messages.length),
        pairwise(), // Emits [previous, current]
        // when count increases: display last message,
        // otherwise display previous to last so the user question stay visible
        map(([prev, curr]) => curr > prev),
      )
      .subscribe((displayLast) => {
        if (!this.firstLoadDone) {
          this.requestScroll('bottom');
        } else {
          this.requestScroll({ displayLast });
        }
      });
  }

  onScroll(): void {
    this.checkOverflow();
  }

  copyMessage(message: Message, tooltip: MibTooltipDirective): void {
    const html = marked.parse(message.content, {
      breaks: true,
    }) as string;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const rawText = (temp.textContent || temp.innerText || '').trim();

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(rawText)
        .then(() => this.onCopySuccess(tooltip))
        .catch((err) => {
          console.error('Failed to copy text: ', err);
        });
    }
  }

  handleClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() === 'a') {
      const link = target as HTMLAnchorElement;
      const url = new URL(link.href, window.location.origin);
      event.preventDefault();

      if (this.chatInfo()?.chatId === CASE_LIBRARY_SEARCH_CHAT_ID) {
        const pathWithQuery = `${url.pathname}${url.search}`;
        this.timeLinkClick.emit(pathWithQuery);
        this.analytics.trackEvent(AnalyticsEvents.LibraryChatTimeLinkClicked, {
          link: pathWithQuery,
        });
      } else {
        const ts = url.searchParams.get('ts');
        if (ts) {
          this.analytics.trackEvent(AnalyticsEvents.CaseChatTimeLinkClicked, {
            case_id: this.chatInfo()?.chatId,
          });
          this.timeLinkClick.emit(ts);
        }
      }
    }
  }

  sendHitlAnswer(approved: boolean): void {
    this.service.sendMessage('', approved);
  }

  private checkOverflow(): void {
    const container = this.messageContainer()?.nativeElement;
    if (container) {
      // true if content extends below the visible area
      this.isOverflowing.set(
        container.scrollHeight > container.clientHeight &&
          container.scrollTop + container.clientHeight < container.scrollHeight - 2,
      );
    }
  }

  private onCopySuccess(tooltip: MibTooltipDirective) {
    this.copyTooltip.set('Copied!');
    // required for the tooltip to be shown on touch screen as well
    tooltip.show();
    setTimeout(() => {
      tooltip.hide();
      setTimeout(() => this.copyTooltip.set('Copy'), 200);
    }, 1000);
  }

  /**
   * Apply a scroll intent immediately if the chat is visible, otherwise defer it
   * until the chat becomes visible again so scrolls aren't lost while hidden.
   */
  private requestScroll(intent: 'bottom' | { displayLast: boolean }): void {
    if (!this.active()) {
      this.pendingScroll = intent;
      return;
    }
    this.pendingScroll = null;
    this.performScroll(intent);
  }

  private performScroll(intent: 'bottom' | { displayLast: boolean }): void {
    if (intent === 'bottom') {
      this.scrollToBottom();
    } else {
      this.scrollToLastMessage(intent.displayLast);
    }
  }

  private scrollToBottom() {
    setTimeout(() => {
      const messageContainer = this.messageContainer()?.nativeElement;
      if (messageContainer) {
        messageContainer.scrollTo({
          top: messageContainer.scrollHeight,
          behavior: 'instant',
        });
        this.firstLoadDone = true;
      }
    });
  }

  /**
   * Scrolls to the target message in the chat container with smooth behavior.
   * Only scrolls if the target message is not already aligned to the top of the container.
   * @param displayLast - If true, scrolls to the last message; if false, scrolls to the second-to-last message (keeps user question visible). Defaults to true.
   */
  private scrollToLastMessage(displayLast = true) {
    setTimeout(() => {
      const messageContainer = this.messageContainer()?.nativeElement;
      if (messageContainer) {
        const messageElements = messageContainer.querySelectorAll<HTMLElement>('.markdown-message');
        const lastMessageIndex = displayLast
          ? messageElements.length - 1
          : messageElements.length - 2;
        const last = messageElements.item(lastMessageIndex);
        const isUserMessageOnTop = this.isElementAtTop(last, messageContainer);
        if (!isUserMessageOnTop) {
          last.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  private isElementAtTop(element: HTMLElement, container: HTMLElement): boolean {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return elementRect.top <= containerRect.top;
  }
}
